// FMO Retention Analytics - Trends, carrier breakdown, churn reasons
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, TrendingUp, BarChart3, PieChart } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_ANALYTICS_TOUR_STEPS = [
  { type: 'splash', title: 'Retention Analytics', description: 'Deep dive into retention trends across your entire network. See monthly patterns, carrier performance, churn reasons, and agency-level breakdowns — all in one view.' },
  { target: 'fmo-analytics-trends', emoji: '📈', title: 'Monthly Trends', description: '12-month retention bar chart. Each bar shows total client count for that month. Hover for details including retention percentage and churn count.', position: 'bottom' },
  { target: 'fmo-analytics-carrier', emoji: '🏥', title: 'By Carrier', description: 'Retention rate per carrier with color-coded bars. Green (90%+), yellow (80-90%), red (<80%). Use this to identify carriers where your agencies struggle most.', position: 'right' },
  { target: 'fmo-analytics-churn', emoji: '📉', title: 'Churn Reasons', description: 'Why clients are leaving — Price, Coverage, Service, Life Event, Competition, and more. The most common reason tells you where to focus your agency training.', position: 'left' },
  { target: 'fmo-analytics-byagency', emoji: '🏢', title: 'By Agency', description: 'Per-agency client counts and at-risk numbers. Compare agencies side by side to identify best practices and struggling teams.', position: 'left' },
  { type: 'celebration', title: 'Data-Driven Decisions!', description: 'Review analytics monthly. Share carrier-level insights with agency owners — they often don\'t see cross-agency patterns that you can spot at the FMO level.' },
];

export default function FmoAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [anTourStep, setAnTourStep] = useState(0);
  const [showAnTour, setShowAnTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-analytics-tour-dismissed')) setShowAnTour(true);
  }, []);
  const advanceAnTour = useCallback(() => { setAnTourStep(s => { if (s + 1 >= FMO_ANALYTICS_TOUR_STEPS.length) { localStorage.setItem('fmo-analytics-tour-dismissed', 'true'); setShowAnTour(false); return 0; } return s + 1; }); }, []);
  const backAnTour = useCallback(() => setAnTourStep(s => Math.max(0, s - 1)), []);
  const dismissAnTour = useCallback(() => { localStorage.setItem('fmo-analytics-tour-dismissed', 'true'); setShowAnTour(false); }, []);
  useEffect(() => {
    if (!showAnTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissAnTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceAnTour(); if (e.key === 'ArrowLeft') backAnTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showAnTour, advanceAnTour, backAnTour, dismissAnTour]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/analytics?months=12`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) { console.error('[FMO Analytics]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  const trends = data?.trends || [];
  const byCarrier = data?.byCarrier || [];
  const byAgency = data?.byAgency || [];
  const churnReasons = data?.churnReasons || [];
  const maxTrend = Math.max(...trends.map(t => t.total), 1);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2">
                <TrendingUp className="text-green-600" /> Retention Analytics
              </h1>
              <p className="text-sm text-gray-500">Trends, carrier performance, churn analysis</p>
            </div>
          </div>
          <button onClick={fetchAnalytics} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Monthly Retention Trend */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-analytics-trends">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><BarChart3 size={18} /> Monthly Trends</h3>
          <div className="flex items-end gap-1 h-40 overflow-x-auto">
            {trends.map(t => (
              <div key={t.month} className="flex flex-col items-center min-w-[40px]" title={`${t.month}: ${t.total} clients, ${t.retention}% retention`}>
                <div className="w-8 bg-amber-400 rounded-t transition-all" style={{ height: `${(t.total / maxTrend) * 120}px` }} />
                <span className="text-[10px] text-gray-400 mt-1">{t.month.split('-')[1]}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-6 text-xs text-gray-400">
            <span>Latest: {trends[trends.length - 1]?.retention || '--'}% retention</span>
            <span>{trends[trends.length - 1]?.churned || 0} churned last month</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Carrier */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-analytics-carrier">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><PieChart size={18} /> By Carrier</h3>
            <div className="space-y-3">
              {byCarrier.slice(0, 10).map(c => (
                <div key={c.carrier}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{c.carrier}</span>
                    <div className="text-right">
                      <span className="text-sm font-bold">{c.retention}%</span>
                      <span className="text-xs text-gray-400 ml-2">{c.total} clients</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${c.retention >= 90 ? 'bg-green-500' : c.retention >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${c.retention}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Churn Reasons + By Agency */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-analytics-churn">
              <h3 className="font-semibold text-gray-900 mb-4">Churn Reasons</h3>
              {churnReasons.length === 0 ? (
                <p className="text-sm text-gray-400">No churn data recorded</p>
              ) : (
                <div className="space-y-2">
                  {churnReasons.map(r => (
                    <div key={r.reason} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{r.reason}</span>
                      <span className="text-sm font-bold text-gray-900">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-analytics-byagency">
              <h3 className="font-semibold text-gray-900 mb-4">By Agency</h3>
              <div className="space-y-3">
                {byAgency.map(a => (
                  <div key={a.agencyId} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{a.agencyName}</span>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-600">{a.total} clients</span>
                      {a.atRisk > 0 && <span className="text-red-600">{a.atRisk} at risk</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAnTour && (
        <TourBubble stepData={FMO_ANALYTICS_TOUR_STEPS[anTourStep]} stepIndex={anTourStep} totalSteps={FMO_ANALYTICS_TOUR_STEPS.length}
          onNext={advanceAnTour} onBack={backAnTour} onDismiss={dismissAnTour}
          splashIcon={<TrendingUp className="w-10 h-10 text-green-600" />} maskId="fmo-analytics-tour-mask" />
      )}
    </div>
  );
}
